import { Button, Stack } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { addressValidationSchema } from '@components/FormAddressFields/config';
import { IAddress } from '@components/FormAddressFields/types';
import { yupResolver } from '@hookform/resolvers/yup';
import { countries } from 'src/assets/data';
import { RHFCheckbox, RHFTextField } from 'src/components/HookForm';
import { suggestedCountry } from 'src/resources/countries';

import { AddressFormFields, AddressFormValues, addressFormValidationSchema } from '@src/forms/address';

export interface FormUserAddressState extends AddressFormValues {
  description: string;
  default: boolean;
}

interface FormUserAddressProps {
  onSubmit: (values: FormUserAddressState) => void | Promise<any>;
  onCancel: VoidFunction;
  address?: IAddress;
  disabled?: boolean;
  showDefaultControl?: boolean;
  lockDefaultOnTrue?: boolean;
}

const validationSchema = addressValidationSchema
  .shape({
    description: Yup.string().required('Address Name is required'),
    default: Yup.boolean().required(),
  })
  .concat(addressFormValidationSchema);

export const countryCodeToString = (form: FormUserAddressState) => ({
  ...form,
  countryCode: form.countryCode.code,
});

export const FormUserAddress = ({
  address,
  disabled = false,
  onSubmit,
  onCancel,
  showDefaultControl = true,
  lockDefaultOnTrue = false,
}: FormUserAddressProps) => {
  const defaultCountry = (!!address && countries.find(({ code }) => code === address.countryCode)) || suggestedCountry;

  const formRHF = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      countryCode: defaultCountry,
      addressLine1: address?.addressLine1 || '',
      addressLine2: address?.addressLine2 || '',
      locality: address?.locality || '',
      administrativeArea: address?.administrativeArea || '',
      postalCode: address?.postalCode || '',
      description: address?.description || '',
      default: address?.default || lockDefaultOnTrue || false,
    },
  });

  const onSaveClick = async (form: FormUserAddressState) => {
    await onSubmit(form);
    formRHF.reset();
  }

  return (
    <FormProvider {...formRHF}>
      <form noValidate>
        <Stack direction="column" gap={3}>
          <RHFTextField name="description" label="Address Name" required />
          <AddressFormFields />
          {showDefaultControl && (
            <RHFCheckbox disabled={lockDefaultOnTrue} label="Use address as default" name="default" />
          )}
          <Stack direction="row-reverse" spacing={2}>
            <Button variant="contained" color="primary" disabled={disabled} onClick={formRHF.handleSubmit(onSaveClick)}>
              Save Address
            </Button>
            <Button type="button" onClick={onCancel} color="inherit" variant="text" disabled={disabled}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </form>
    </FormProvider>
  );
};
